import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import axios from "axios";
import config from "../config/config.json";
import { useAuth } from "../context/AuthContext";
import {
  artworkLabels,
  categoryLabels,
  dailySeries,
  destinationLabels,
  formatShareTime,
  initialFilters,
  statusColors,
  statusLabels,
} from "./greetingShareUI";

const panelSx = {
  p: { xs: 2, md: 3 },
  bgcolor: "white",
  border: "1px solid #E5E7EB",
  borderRadius: 3,
};
const emptySummary = {
  totalAttempts: 0,
  targetedActions: 0,
  completedActions: 0,
  uniqueSharers: 0,
};

export default function GreetingSharesPage() {
  const { token } = useAuth();
  const [draft, setDraft] = useState(initialFilters);
  const [query, setQuery] = useState(() => ({ ...initialFilters(), page: 1 }));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterError, setFilterError] = useState("");
  const requestId = useRef(0);
  const fetchReport = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${config.api_base}/api/admin/greeting-shares`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { ...query, limit: 20 },
        }
      );
      if (id === requestId.current) setReport(response.data.data);
    } catch (e) {
      if (id === requestId.current) {
        setError(
          e?.response?.data?.message || "Kart paylaşım raporu alınamadı."
        );
        setReport(null);
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [query, token]);
  useEffect(() => {
    fetchReport();
    return () => {
      requestId.current += 1;
    };
  }, [fetchReport]);
  const series = useMemo(
    () => dailySeries(report?.daily || [], query.from, query.to),
    [report, query.from, query.to]
  );
  const summary = report?.summary || emptySummary;
  const field = (name) => ({
    value: draft[name],
    onChange: (event) =>
      setDraft((previous) => ({ ...previous, [name]: event.target.value })),
  });
  const select = (name, label, options) => (
    <TextField key={name} select label={label} size="small" {...field(name)}>
      <MenuItem value="all">Tümü</MenuItem>
      {Object.entries(options).map(([value, text]) => (
        <MenuItem key={value} value={value}>
          {text}
        </MenuItem>
      ))}
    </TextField>
  );
  const applyFilters = (event) => {
    event.preventDefault();
    const days = (Date.parse(draft.to) - Date.parse(draft.from)) / 86400000 + 1;
    if (!Number.isFinite(days) || days < 1 || days > 366) {
      setFilterError(
        "Başlangıç ve bitiş tarihleri arasında en fazla 366 gün seçebilirsiniz."
      );
      return;
    }
    setFilterError("");
    setQuery({ ...draft, search: draft.search.trim(), page: 1 });
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ShareOutlinedIcon />
            Kart Paylaşımları
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Kim, ne zaman ve hangi hedefe yöneldi? Tarihler Türkiye saatindedir.
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchReport}
          disabled={loading}
        >
          Yenile
        </Button>
      </Stack>
      <Alert severity="info" sx={{ mb: 3 }}>
        Hedef seçimi, görselin alıcıya teslim edildiğini doğrulamaz. Android
        hedef seçimini, iOS ise bildirdiği tamamlanma veya iptal sonucunu
        kaydeder. Hedef veya sonuç bildirilmezse bilinmiyor olarak gösterilir.
        Kayıtlar yeni mobil sürümle başlar; geçmiş paylaşımlar geriye dönük
        sayılmaz.
      </Alert>
      <Box component="form" onSubmit={applyFilters} sx={{ ...panelSx, mb: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          <TextField
            label="Başlangıç tarihi"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            required
            {...field("from")}
          />
          <TextField
            label="Bitiş tarihi"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            required
            {...field("to")}
          />
          {select("destination", "Hedef uygulama", destinationLabels)}
          {select("platform", "İşletim sistemi", {
            ios: "iOS",
            android: "Android",
          })}
          {select("category", "Kart kategorisi", categoryLabels)}
          {select("status", "İşlem sonucu", statusLabels)}
          <TextField
            label="Kullanıcı adı veya e-posta"
            size="small"
            inputProps={{ maxLength: 100 }}
            {...field("search")}
          />
          <Button variant="contained" type="submit" disabled={loading}>
            Filtrele
          </Button>
        </Box>
        {filterError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {filterError}
          </Alert>
        )}
      </Box>
      {error && (
        <Alert
          severity="error"
          action={<Button onClick={fetchReport}>Tekrar dene</Button>}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}
      {!error && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            {[
              [
                "Paylaşım denemesi",
                summary.totalAttempts,
                "Hazırlama ve sonuçlanmayan işlemler dahil",
              ],
              [
                "Hedef seçimi / tamamlanan",
                summary.targetedActions,
                "Android seçimi ve iOS tamamlanması",
              ],
              [
                "Sistemce tamamlanan",
                summary.completedActions,
                "Yalnızca iOS geri bildirimi",
              ],
              [
                "İşlem yapan kullanıcı",
                summary.uniqueSharers,
                "Hedef seçen veya tamamlayan tekil kullanıcı",
              ],
            ].map(([label, value, hint]) => (
              <Box key={label} sx={panelSx}>
                <Typography color="text.secondary" variant="body2">
                  {label}
                </Typography>
                <Typography sx={{ fontSize: 30, fontWeight: 800, my: 1 }}>
                  {loading ? "…" : Number(value || 0).toLocaleString("tr-TR")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {hint}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <Box sx={{ ...panelSx, minWidth: 0 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Günlük hareket
              </Typography>
              <Box sx={{ height: 250, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) =>
                        value.slice(5).split("-").reverse().join(".")
                      }
                    />
                    <YAxis allowDecimals={false} />
                    <ChartTooltip />
                    <Legend />
                    <Bar
                      dataKey="attempts"
                      name="Deneme"
                      fill="#CBD5E1"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="actions"
                      name="Hedef / tamamlanma"
                      fill="#2563EB"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
            <Box sx={panelSx}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Hedeflere göre işlemler
              </Typography>
              {report?.destinations?.length ? (
                report.destinations.map((item) => (
                  <Stack
                    key={item._id}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ py: 1.25, borderBottom: "1px solid #F1F5F9" }}
                  >
                    <Typography>
                      {destinationLabels[item._id] || item._id}
                    </Typography>
                    <Chip label={item.count} size="small" />
                  </Stack>
                ))
              ) : (
                <Typography color="text.secondary">
                  Henüz hedef bildirimi yok.
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={panelSx}>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">İşlem kayıtları</Typography>
              <Typography color="text.secondary">
                {report?.pagination?.total || 0} kayıt
              </Typography>
            </Stack>
            {loading ? (
              <Box sx={{ textAlign: "center", p: 5 }}>
                <CircularProgress aria-label="Paylaşımlar yükleniyor" />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table
                    size="small"
                    sx={{ minWidth: 860 }}
                    aria-label="Kart paylaşım kayıtları"
                  >
                    <TableHead>
                      <TableRow>
                        {[
                          "Tarih",
                          "Kullanıcı",
                          "Kart",
                          "Hedef",
                          "Sonuç",
                          "Sistem",
                        ].map((label) => (
                          <TableCell key={label}>{label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(report?.items || []).map((item) => (
                        <TableRow key={`${item.userId}:${item.shareId}`} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatShareTime(item.startedAt)}
                          </TableCell>
                          <TableCell>
                            <Typography
                              component={RouterLink}
                              to={`/users/${item.userId}`}
                              sx={{
                                color: "#2563EB",
                                textDecoration: "none",
                                fontWeight: 600,
                              }}
                            >
                              {item.userName}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {item.userEmail}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {categoryLabels[item.category]} ·{" "}
                              {artworkLabels[item.artwork] || item.artwork}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.customMessage
                                ? "Özel mesaj"
                                : item.messageId || "Hazır mesaj"}
                              {item.hasSignature ? " · İsim eklendi" : ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                item.targetIdentifier ||
                                "İşletim sistemi hedef bildirmedi"
                              }
                            >
                              <span>
                                {destinationLabels[item.destination] ||
                                  "Hedef bilinmiyor"}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusLabels[item.status] || item.status}
                              color={statusColors[item.status] || "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {item.platform === "ios" ? "iOS" : "Android"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!report?.items?.length && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{ textAlign: "center", py: 5 }}
                          >
                            Bu filtrelerle kayıt bulunamadı.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack alignItems="center" sx={{ mt: 3 }}>
                  <Pagination
                    count={report?.pagination?.totalPages || 1}
                    page={query.page}
                    onChange={(_, page) =>
                      setQuery((previous) => ({ ...previous, page }))
                    }
                  />
                </Stack>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
